// Lib
import React from 'react';
import PropTypes from 'prop-types';
import * as Immutable from 'immutable';

// Utils
import * as elementFactory from '../../../../common/elements/elementRegistry';
import * as pointsLib from '../../../../common/maths/geometry/point';
import { getTranslationStyles } from '../../../utils/cssUtil';

// Components
import ElementDragPreviewPositioner from '../../dnd/ElementDragPreviewPositioner';
import DraggableElementCreationTool from '../../dnd/DraggableElementCreationTool';
import Icon from '../../../components/icons/Icon';

// Constants
import { CARD_TYPE } from '../../../../common/cards/cardConstants';
import { DRAG_PREVIEW_ID } from '../../../reducers/draggingConstants';

// Styles
import './CardClipperButton.scss';

// Clipped text is saved on mousedown in the cardClipperEditorStateManager
const getDefaultContent = ({ clippedText }) => ({
    textContent: clippedText,
});

const getElementScaledCustomDragOffset = ({ selectionHeight, gridSize, getContextZoomScale }) => ({
    x: -29 * gridSize * getContextZoomScale(),
    y: 2 - selectionHeight / 2,
});

const getElementScaledGrabOffset = (props) => {
    return pointsLib.scale(-1, getElementScaledCustomDragOffset(props));
};

// We need to keep editing on drag, otherwise the original card will try to save the editor state update
// that we made to highlight the clipped text (due to the Milanote Editor's "isNoLongerEditing" prop comparison).
const CardClipperButton = (props) => {
    const { currentBoardId, gridSize, getContextZoomScale } = props;

    let mouseDownPreview = null;
    if (props.isClipping) {
        const elementDragPreviewData = Immutable.fromJS(
            elementFactory.createElementObject({
                id: DRAG_PREVIEW_ID,
                elementType: CARD_TYPE,
                content: getDefaultContent(props),
            }),
        );

        const zoomScale = getContextZoomScale();

        const scaledCustomDragOffset = getElementScaledCustomDragOffset(props);
        const unscaledCustomDragOffset = pointsLib.reverseScale(zoomScale, scaledCustomDragOffset);

        const mouseDownPreviewItem = {
            unscaledElementOffsetsMap: {
                // Shifts the drag preview up half the height of the selection area and to the right so the card text
                // is in a similar spot to where it's being clipped from
                [DRAG_PREVIEW_ID]: unscaledCustomDragOffset,
            },
            grabbedElement: elementDragPreviewData,
            element: elementDragPreviewData,
        };

        mouseDownPreview = (
            <ElementDragPreviewPositioner
                item={mouseDownPreviewItem}
                currentBoardId={currentBoardId}
                element={elementDragPreviewData}
                isPresentational
                gridSize={gridSize}
            />
        );
    }

    return (
        <div className="CardClipperButton" style={getTranslationStyles({ x: 0, y: props.y })}>
            <DraggableElementCreationTool
                {...props}
                getElementScaledCustomDragOffset={getElementScaledCustomDragOffset}
                getElementScaledGrabOffset={getElementScaledGrabOffset}
                getDefaultContent={getDefaultContent}
                elementType={CARD_TYPE}
            >
                {mouseDownPreview}
                <div className="draggable-shadow">
                    <Icon name="element-card" />
                </div>
                <Icon name="element-card" />
            </DraggableElementCreationTool>
        </div>
    );
};

CardClipperButton.propTypes = {
    y: PropTypes.number,
    selectionHeight: PropTypes.number,
    connectDragSource: PropTypes.func,
    connectDragPreview: PropTypes.func,

    currentBoardId: PropTypes.string,
    isClipping: PropTypes.bool,
    gridSize: PropTypes.number,
    getContextZoomScale: PropTypes.func,
};

export default CardClipperButton;
